import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Link, useParams, useRouter } from '@tanstack/react-router';
import { useTenant } from '../../lib/api/hooks.js';
import { StatusBadge, ErrorBanner, Stat, StatGroup, CardHead } from '../../components/ui/Primitives.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { useCdrStats, useExtensions } from '../../lib/api/hooks.js';
import { useCallPlans, useTenantTrunks } from '../../lib/api/hooks-products.js';
import { useSetCallPlan, useSetModule, useTenantModules, useUpdateTenant } from '../../lib/api/hooks-billing.js';
import { Switch } from '../../components/ui/Form.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Icon } from '../../components/ui/Icon.js';
import type { Tenant } from '@arcnet/contracts';
import { formatMoney, formatNumber } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import { useSubroute, childSubroute, SubrouteProvider } from '../../lib/subroute.js';

import { ExtensionsPage } from '../extensions/ExtensionsPage.js';
import { useTenantLifecycle } from '../../lib/api/hooks.js';
import { BillingPage } from '../billing/BillingPage.js';
import { AccountTrunksTab } from './AccountTrunksTab.js';
import { TeamsTab } from '../manage/TeamsTab.js';
import { usePlatformFeatures } from '../../lib/api/hooks-features.js';
import { HealthBadge, RegistrationBadge } from '../carriers/TrunkLive.js';
import { NumbersTab } from '../manage/NumbersTab.js';
import { UsersTab } from '../manage/UsersTab.js';
import { AccountTransactionsTab } from './AccountTransactionsTab.js';
import { AccountNotesTab, AccountContactsTab } from './AccountRecordTabs.js';
import { AccountDocsTab } from './AccountDocsTab.js';
import { FraudHoldBanner } from '../fraud/FraudHoldBanner.js';

/**
 * One customer account, as the PLATFORM sees it.
 *
 * This is the MSP side of a deliberate split. Everything commercial or
 * provisioning-shaped about a customer -- what they are sold, what they owe,
 * which carriers carry them, who may administer them -- belongs here, where
 * staff reach it directly. Everything call-flow-shaped -- IVRs, queues,
 * schedules, routing -- belongs to the customer in /manage, because it is theirs
 * to change and it is not what support is looking at when an invoice is queried.
 *
 * WHY THE TABS ARE HERE AND NOT BEHIND "log in as the customer"
 *
 * Impersonation was the obvious way to do this and is the wrong one. It makes
 * every staff action indistinguishable from the customer's own in the audit
 * trail, it means holding a session that can do more than the task needs, and it
 * makes the simplest question -- "what is this account's balance?" -- a
 * multi-step context switch. Staff read through to the account instead, under
 * their own identity and their own permissions.
 */

type TabKey =
  | 'details' | 'extensions' | 'numbers' | 'trunks' | 'teams' | 'tms'
  | 'billing' | 'transactions' | 'contacts' | 'notes' | 'docs';

/**
 * Named after the account record, not after our tables.
 *
 * Staff arriving from another platform look for "Billing Information" and
 * "Transactions"; they do not look for "billing_accounts" and "movements". The
 * permission on each is the one guarding the data behind it, so a tab nobody may
 * read is not offered and then refused.
 */
const TABS: Array<{ key: TabKey; label: string; group: string; permission?: string }> = [
  { key: 'details',      label: 'Details',             group: 'Account' },
  { key: 'extensions',   label: 'Extensions',          group: 'Telephony', permission: 'extension:read' },
  { key: 'numbers',      label: 'DID & Routing',       group: 'Telephony', permission: 'number:read' },
  { key: 'trunks',       label: 'Trunks',              group: 'Telephony', permission: 'trunk:read' },
  // Microsoft Teams calling (docs/44): while the platform has it on for this person (staff only first).
  { key: 'teams',        label: 'Microsoft Teams',     group: 'Telephony', permission: 'teams:read' },
  /*
    TMS is the customer's own portal, so this tab is the people who sign in to
    it. It used to render the TRUNK links under the TMS label, with the portal
    users on a separate "Managers" tab -- one screen misnamed and one duplicated.
  */
  { key: 'tms',          label: 'TMS users',           group: 'People', permission: 'user:read' },
  { key: 'contacts',     label: 'Contacts',            group: 'People' },
  { key: 'billing',      label: 'Billing Information', group: 'Money', permission: 'billing:read' },
  { key: 'transactions', label: 'Transactions',        group: 'Money', permission: 'billing:read' },
  { key: 'notes',        label: 'Notes',               group: 'Account' },
  { key: 'docs',         label: 'Docs',                group: 'Account' },
];
/** The side list's groups, in order (owner, 3 October 2026: nine tabs became a side list). */
const GROUPS = ['Account', 'Telephony', 'People', 'Money'];

export function AccountDetailPage() {
  const { accountId } = useParams({ strict: false }) as { accountId: string };
  const account = useTenant(accountId);
  const can = useSession((s) => s.can);
  /**
   * The open tab is the path below the account -- /accounts/meridian/extensions
   * -- so a link names the tab, and the seat inside it, not only the customer.
   * It was component state, so every link opened on Details.
   */
  const route = useSubroute();
  const tab: TabKey = TABS.some((x) => x.key === route.rest[0]) ? route.rest[0] as TabKey : 'details';
  const setTab = (k: TabKey) => route.go(k === 'details' ? [] : [k], { replace: true });
  const t = account.data;
  const router = useRouter();
  const rest = route.rest.join('/');

  /**
   * Accounts are addressed by slug: /accounts/meridian, not by the id. A link
   * or bookmark that still uses the id works, and is rewritten in place once
   * the account has loaded, so the address bar ends up with the short one.
   */
  useEffect(() => {
    if (t?.slug && accountId !== t.slug) {
      router.history.replace(`/accounts/${t.slug}${rest ? `/${rest}` : ''}`);
    }
  }, [t?.slug, accountId, rest, router]);

  if (account.error) return <ErrorBanner error={account.error} />;

  const features = usePlatformFeatures().data;
  const visible = TABS.filter((x) => (!x.permission || can(x.permission)) && (x.key !== 'teams' || features?.teams === true));

  return (
    <>
      <Link to="/accounts" className="arc-crumb">
        <Icon name="arrowLeft" size={15} /> Customer accounts
      </Link>

      <div className="arc-page-head">
        <div>
          <div className="arc-row" style={{ gap: 9 }}>
            <h1 className="arc-h1">{t?.name ?? '…'}</h1>
            {t && <StatusBadge status={t.status} />}
          </div>
          <p className="arc-mono arc-subtle" style={{ marginTop: 3 }}>{t?.sip_domain}</p>
        </div>

        {/*
          Call routing still opens in its own tab. It is the CUSTOMER's surface,
          it is a long working session, and mixing it into this chrome would
          invite wandering out of a half-finished flow.
        */}
        <a
          className="arc-btn"
          /* The slug, so the link somebody pastes into a ticket is readable
             and short. Falls back to the id until the tenant has loaded. */
          href={`/manage/${t?.slug ?? accountId}`}
          target="_blank"
          rel="noopener"
        >
          Call routing &amp; features
          <Icon name="external" size={13} />
        </a>
      </div>

      {/* Every section in sight at once, as on the extension page: a strip of nine tabs did not fit. */}
      <div className="arc-seat">
        <nav className="arc-card arc-seat-nav" aria-label="Account sections">
          {GROUPS.map((g) => {
            const items = visible.filter((x) => x.group === g);
            if (items.length === 0) return null;
            return (
              <div key={g} className="arc-seat-nav-group">
                <div className="arc-rail-section">{g}</div>
                {items.map((x) => (
                  <button key={x.key} type="button" className="arc-rail-item"
                    aria-current={tab === x.key ? 'page' : undefined}
                    onClick={() => setTab(x.key)}>
                    {x.label}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        {/* The address may hold a slug; every section's API calls need the id. */}
        <div style={{ minWidth: 0 }}>
          {t && (
            <SubrouteProvider value={childSubroute(route, [tab])}>
              <TabBody tab={tab} tenantId={t.id} tenant={t} />
            </SubrouteProvider>
          )}
        </div>
      </div>
    </>
  );
}

function TabBody({ tab, tenantId, tenant }: {
  tab: TabKey; tenantId: string; tenant: Tenant | null;
}) {
  switch (tab) {
    case 'details':      return <Overview tenantId={tenantId} tenant={tenant} />;
    /*
      Reused wholesale rather than reimplemented. These are the same screens the
      customer sees, and a second copy for staff is a second place for the
      extension list to be wrong.
    */
    // scope="platform": the MSP record configures what a seat is and what it
    // costs. Voicemail delivery, follow-me and queue membership are the
    // customer's to run, in their own console.
    case 'extensions':   return <ExtensionsPage tenantId={tenantId} embedded scope="platform" />;
    case 'numbers':      return <NumbersTab tenantId={tenantId} />;
    case 'trunks':       return <AccountTrunksTab tenantId={tenantId} />;
    case 'teams':        return <TeamsTab tenantId={tenantId} />;
    case 'tms':          return (
      <UsersTab tenantId={tenantId} title="TMS users"
        subtitle="The customer’s people who sign in to their TMS portal to manage this PBX." />
    );
    case 'billing':      return <BillingPage tenantId={tenantId} embedded />;
    case 'transactions': return <AccountTransactionsTab tenantId={tenantId} />;
    case 'contacts':     return <AccountContactsTab tenantId={tenantId} />;
    case 'notes':        return <AccountNotesTab tenantId={tenantId} />;
    case 'docs':         return <AccountDocsTab tenantId={tenantId} />;
  }
}

function Overview({ tenantId, tenant }: { tenantId: string; tenant: Tenant | null }) {
  const stats = useCdrStats(tenantId);
  const extensions = useExtensions(tenantId, { limit: 200 });
  const used = extensions.data?.data.length ?? 0;
  const limit = tenant?.max_extensions ?? 0;

  return (
    <>
      <FraudHoldBanner tenantId={tenantId} />
      <StatGroup>
        {/* A dash while loading, not a zero -- "0 / 50 extensions, in use" and
            "0 calls in 30 days" are both claims about a live account, and for
            the second before the answer lands they are claims we cannot make. */}
        <Stat bare
          label="Extensions"
          value={extensions.data ? `${used}${limit ? ` / ${limit}` : ''}` : '—'}
          sub={limit && used >= limit ? 'at the limit' : 'in use'}
          tone={limit && used >= limit ? 'warn' : undefined}
        />
        <Stat bare label="Calls (30 days)"
          value={stats.data ? formatNumber(stats.data.total_calls) : '—'}
          sub={stats.data ? `${stats.data.answer_rate_pct}% answered` : undefined} />
        <Stat bare label="Spend (30 days)"
          value={formatMoney(stats.data?.spend ?? null, stats.data?.currency ?? 'ZAR')} />
        <Stat bare label="Channels" value={tenant?.max_concurrent_calls ?? '—'}
          sub={`${tenant?.max_cps ?? '—'} calls/sec`} />
      </StatGroup>

      <TrunkSummary tenantId={tenantId} />

      <div className="arc-card">
        <CardHead title="Account details" />
        <div className="arc-card-body">
          <dl className="arc-kv">
            <dt>Slug</dt><dd className="arc-mono">{tenant?.slug ?? '—'}</dd>
            <dt>Timezone</dt><dd>{tenant?.timezone ?? '—'}</dd>
            <dt>Country</dt><dd>{tenant?.country_code ?? '—'}</dd>
            <dt>Currency</dt><dd>{tenant?.currency ?? '—'}</dd>
            <dt>Created</dt>
            <dd>{tenant ? new Date(tenant.created_at).toLocaleDateString('en-ZA') : '—'}</dd>
          </dl>
        </div>
      </div>

      {tenant && <Plan tenantId={tenantId} tenant={tenant} />}
      {tenant && <Limits tenantId={tenantId} tenant={tenant} used={used} />}
      <Modules tenantId={tenantId} />
      {tenant && <Lifecycle tenantId={tenantId} tenant={tenant} />}
    </>
  );
}

/**
 * The account's call plan (owner, 25 September 2026: "how do i select a calling plan for an
 * account?"): its monthly fee and included minutes, and the rate sheet its calls are priced on --
 * both set together by the API. Plans in the account's own currency only. billing:write, step-up,
 * admin addresses only (nginx), like every billing change.
 */
function Plan({ tenantId, tenant }: { tenantId: string; tenant: Tenant }) {
  const can = useSession((s) => s.can);
  const plans = useCallPlans();
  const setPlan = useSetCallPlan(tenantId);
  const step = useStepUp();
  const current = tenant.call_plan_id ?? '';
  const [choice, setChoice] = useState(current);
  useEffect(() => setChoice(current), [current]);
  const [saved, setSaved] = useState(false);

  const all = plans.data?.data ?? [];
  const offered = all.filter((p) => p.currency === tenant.currency && (p.is_active || p.id === current));
  const now = all.find((p) => p.id === current);
  const picked = all.find((p) => p.id === choice);
  const writer = can('billing:write');

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Call plan" />
      <div className="arc-card-body">
        {!writer ? (
          <dl className="arc-kv">
            <dt>Plan</dt><dd>{now?.name ?? 'None'}</dd>
            <dt>Rate sheet</dt><dd>{now?.rate_deck_name ?? '—'}</dd>
          </dl>
        ) : (
          <>
            <div className="arc-row" style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="arc-field" style={{ margin: 0, minWidth: 260 }}>
                <label className="arc-label" htmlFor="acct-plan">Plan</label>
                <select id="acct-plan" className="arc-select" value={choice}
                  onChange={(e) => { setSaved(false); setChoice(e.target.value); }}>
                  <option value="">No plan</option>
                  {offered.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({formatMoney(p.monthly_fee, p.currency)} a month{p.is_active ? '' : ', switched off'})
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
                disabled={choice === current || setPlan.isPending || step.busy}
                onClick={() => void step.run(() => setPlan.mutateAsync(choice || null)).then((r) => { if (r) setSaved(true); })}>
                {setPlan.isPending ? 'Saving…' : 'Save'}
              </button>
              {saved && choice === current && <span className="arc-muted" style={{ fontSize: 12.5 }}>Saved</span>}
            </div>
            <div className="arc-hint" style={{ marginTop: 6 }}>
              {picked
                ? `${picked.included_minutes ? `${formatNumber(picked.included_minutes)} minutes included. ` : ''}`
                  + (picked.rate_deck_name ? `Calls are priced on ${picked.rate_deck_name}.` : 'This plan has no rate sheet; the account keeps the one it has.')
                : 'No monthly fee. Calls are priced on the account\'s current rate sheet.'}
              {plans.data && offered.length === 0 && ` No active plans in ${tenant.currency}: make one under Products, Call plans.`}
            </div>
          </>
        )}
        {setPlan.error && <ErrorBanner error={setPlan.error} />}
        <StepUpPrompt step={step} what="Changing the call plan">
          It changes what this account pays. Confirm with your password.
        </StepUpPrompt>
      </div>
    </div>
  );
}

/**
 * The ceilings on an account: how many seats it may create, and how much of the switch it may
 * use at once.
 *
 * The seat limit has been enforced since 0003 and shown on three screens as "12 / 50", and until
 * now it could only be changed with SQL -- the field parsed and was dropped before the update
 * (owner, 25 September 2026). It is a commercial limit rather than a telephony setting, so it
 * lives on the operator's account record and needs tenant:write.
 *
 * Saved with its own button (owner, 25 September 2026: "theres no save button"): saving on blur
 * worked but said nothing, so it read as broken. Never on every keystroke -- a number being typed
 * passes through 1 and 5 on its way to 150.
 */
function Limits({ tenantId, tenant, used }: {
  tenantId: string; tenant: Tenant; used: number;
}) {
  const can = useSession((s) => s.can);
  const update = useUpdateTenant();
  const [limit, setLimit] = useState(String(tenant.max_extensions ?? ''));
  const [saved, setSaved] = useState(false);
  if (!can('tenant:write')) return null;

  const n = Number(limit);
  const valid = Number.isInteger(n) && n >= 1 && n <= 100_000;
  const dirty = valid && n !== tenant.max_extensions;
  const save = (): void => {
    if (!dirty) return;
    setSaved(false);
    update.mutate({ id: tenantId, max_extensions: n }, { onSuccess: () => setSaved(true) });
  };

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Limits" />
      <div className="arc-card-body">
        <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div className="arc-field" style={{ margin: 0, maxWidth: 200 }}>
            <label className="arc-label" htmlFor="max-ext">Extensions allowed</label>
            <input id="max-ext" className="arc-input arc-num" inputMode="numeric" value={limit}
              aria-invalid={!valid || undefined}
              onChange={(e) => { setSaved(false); setLimit(e.target.value.replace(/[^0-9]/g, '')); }}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            <div className={valid ? 'arc-hint' : 'arc-error'}>
              {!valid ? 'A whole number, 1 or more.'
                : n < used ? `${used} already exist; they keep working, but no more can be added.`
                  : `${used} in use.`}
            </div>
            <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!dirty || update.isPending} onClick={save}>
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
              {saved && !dirty && <span className="arc-muted" style={{ fontSize: 12.5 }}>Saved</span>}
            </div>
          </div>
          <div className="arc-field" style={{ margin: 0, maxWidth: 240 }}>
            <span className="arc-label">Channels and call rate</span>
            <div className="arc-hint" style={{ marginTop: 6 }}>
              {tenant.max_concurrent_calls} at once, {tenant.max_cps} a second. Changed with the
              account's homing, since they are what the switch is asked to carry.
            </div>
          </div>
          <HomeSwitch tenantId={tenantId} tenant={tenant} />
        </div>
        {update.error && <ErrorBanner error={update.error} />}
      </div>
    </div>
  );
}

/**
 * The switch the account is on (26 September 2026). Without one nothing of the account reaches a
 * switch: its phones are refused and its trunk never registers. New accounts get one when they are
 * created; this is for one that has none, and says which it is on otherwise.
 */
function HomeSwitch({ tenantId, tenant }: { tenantId: string; tenant: Tenant }) {
  const can = useSession((s) => s.can);
  const update = useUpdateTenant();
  const nodes = useQuery({
    queryKey: ['platform-nodes-pick'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string; status: string }> }>('/v1/platform/nodes'),
    enabled: !tenant.primary_node_id && can('node:read'),
  });
  const active = (nodes.data?.data ?? []).filter((n) => n.status === 'active');
  const [pick, setPick] = useState('');
  const chosen = pick || active[0]?.id || '';

  return (
    <div className="arc-field" style={{ margin: 0, maxWidth: 280 }}>
      <span className="arc-label">Switch</span>
      {tenant.primary_node_id ? (
        <div className="arc-hint arc-mono" style={{ marginTop: 6 }}>{tenant.primary_node_name ?? tenant.primary_node_id}</div>
      ) : (
        <>
          <div className="arc-error" style={{ marginTop: 6 }}>
            On no switch: its phones cannot register and its trunk never reaches one.
          </div>
          {active.length > 0 && (
            <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
              {active.length > 1 && (
                <select className="arc-select" aria-label="Switch" value={chosen} onChange={(e) => setPick(e.target.value)}>
                  {active.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              )}
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!chosen || update.isPending}
                onClick={() => update.mutate({ id: tenantId, primary_node_id: chosen })}>
                {active.length > 1 ? 'Put on switch' : `Put on ${active[0]!.name}`}
              </button>
            </div>
          )}
        </>
      )}
      {update.error && <ErrorBanner error={update.error} />}
    </div>
  );
}

/**
 * What the account has been sold beyond calls (0130): the dialler and Auto QA. Off, a module's menu,
 * pages and API are gone for the account and nothing of it runs; its data is kept. Each seat still
 * needs the matching feature in its package, shown beside the switch.
 */
function Modules({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const modules = useTenantModules(tenantId);
  const set = useSetModule(tenantId);
  const [note, setNote] = useState<string | null>(null);
  if (!modules.data) return null;
  const writer = can('module:write');

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Modules" />
      <div className="arc-card-body" style={{ display: 'grid', gap: 12 }}>
        {modules.data.data.map((m) => (
          <div key={m.key} className="arc-row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Switch id={`mod-${m.key}`} checked={m.enabled} disabled={!writer || set.isPending}
              onChange={async (on) => {
                if (!on && m.key === 'dialler' && !(await confirmAction({
                  title: 'Turn the dialler off?',
                  body: 'Running campaigns are paused, and its pages close for the account. Lists, campaigns and history are kept.',
                  action: 'Turn off', danger: true,
                }))) return;
                setNote(null);
                set.mutate({ module: m.key, enabled: on }, {
                  onSuccess: (r) => { if (r.campaigns_paused) setNote(`${r.campaigns_paused} running campaign${r.campaigns_paused === 1 ? ' was' : 's were'} paused.`); },
                });
              }} />
            <div style={{ flex: 1 }}>
              <label htmlFor={`mod-${m.key}`} style={{ fontWeight: 570, fontSize: 13 }}>{m.label}</label>
              <div className="arc-hint" style={{ marginTop: 2 }}>{m.description}</div>
              <div className="arc-subtle" style={{ fontSize: 12, marginTop: 3 }}>
                {m.seats === 0
                  ? `No extension has it in its package yet.`
                  : `${formatNumber(m.seats)} extension${m.seats === 1 ? ' has' : 's have'} it in ${m.seats === 1 ? 'its' : 'their'} package.`}
                {m.changed_at && ` ${m.enabled ? 'On' : 'Off'} since ${new Date(m.changed_at).toLocaleDateString('en-ZA')}${m.changed_by ? `, ${m.changed_by}` : ''}.`}
              </div>
            </div>
          </div>
        ))}
        {note && <span className="arc-muted" style={{ fontSize: 12.5 }}>{note}</span>}
        {set.error && <ErrorBanner error={set.error} />}
      </div>
    </div>
  );
}

/**
 * Starting and stopping a customer's service.
 *
 * `tenant:suspend` and `tenant:delete` were permission keys with no routes
 * behind them, so on a PREPAID platform there was no way to stop serving a
 * customer who had not paid -- the one control the commercial model rests on.
 *
 * Each state change is its own button with its own confirmation rather than a
 * status dropdown, because they are not variations of one another: suspending
 * is reversible and expected, closing ends the relationship, archiving files
 * away something already finished. A select makes those look interchangeable.
 */
function Lifecycle({ tenantId, tenant }: { tenantId: string; tenant: Tenant }) {
  const can = useSession((s) => s.can);
  const life = useTenantLifecycle(tenantId);
  const [confirming, setConfirming] = useState<null | 'suspend' | 'close' | 'archive'>(null);
  const [reason, setReason] = useState('');

  const maySuspend = can('tenant:suspend');
  const mayEnd = can('tenant:delete');
  if (!maySuspend && !mayEnd) return null;

  const busy = life.suspend.isPending || life.resume.isPending
    || life.close.isPending || life.archive.isPending;

  const done = () => { setConfirming(null); setReason(''); };

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Service" />
      <div className="arc-card-body">
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          {tenant.status === 'suspended'
            ? 'Suspended. Handsets stay registered but can only dial emergency numbers; every other call is refused.'
            : tenant.status === 'closed'
              ? 'Closed. The account is finished, and can be archived once its extensions and numbers are gone.'
              : 'Active. Suspending stops every call except to emergency numbers, immediately.'}
        </p>

        {confirming && confirming !== 'archive' && (
          <div className="arc-field" style={{ marginBottom: 12 }}>
            <label className="arc-label" htmlFor="lc-reason">Why?</label>
            <input id="lc-reason" className="arc-input" autoFocus value={reason}
              placeholder={confirming === 'suspend'
                ? 'Invoice 4471 unpaid, 45 days' : 'Contract ended 30 September'}
              onChange={(e) => setReason(e.target.value)} />
            {/* Mandatory on the server too. "Why is this customer off?" gets
                asked at the worst possible moment, usually by the customer. */}
            <div className="arc-hint">Required, and kept on the audit record.</div>
          </div>
        )}

        <div className="arc-row" style={{ gap: 8 }}>
          {tenant.status === 'suspended' && maySuspend && (
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={busy} onClick={() => life.resume.mutate()}>
              Resume service
            </button>
          )}

          {tenant.status === 'active' && maySuspend && (
            confirming === 'suspend' ? (
              <>
                <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => life.suspend.mutate(reason.trim(), { onSuccess: done })}>
                  {busy ? 'Suspending...' : 'Yes, suspend'}
                </button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={done}>Cancel</button>
              </>
            ) : (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setConfirming('suspend')}>Suspend service</button>
            )
          )}

          {tenant.status !== 'closed' && mayEnd && (
            confirming === 'close' ? (
              <>
                <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => life.close.mutate(reason.trim(), { onSuccess: done })}>
                  {busy ? 'Closing...' : 'Yes, close the account'}
                </button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={done}>Cancel</button>
              </>
            ) : (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setConfirming('close')}>Close account</button>
            )
          )}

          {tenant.status === 'closed' && mayEnd && (
            confirming === 'archive' ? (
              <>
                <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                  disabled={busy}
                  onClick={() => life.archive.mutate(undefined, { onSuccess: done })}>
                  {busy ? 'Archiving...' : 'Yes, archive'}
                </button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={done}>Cancel</button>
              </>
            ) : (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setConfirming('archive')}>Archive</button>
            )
          )}
        </div>

        {confirming === 'archive' && (
          <p className="arc-subtle" style={{ fontSize: 12, margin: '8px 0 0' }}>
            Removes the account from every list. Call records and the audit trail are
            kept -- they are the record that this customer existed and what they were
            charged. Refused while any extension or number remains.
          </p>
        )}
      </div>
    </div>
  );
}

function TrunkSummary({ tenantId }: { tenantId: string }) {
  const links = useTenantTrunks(tenantId);
  const outbound = links.data?.data.filter((l) => l.direction !== 'inbound' && l.is_enabled) ?? [];

  return (
    <div className="arc-card" style={{ marginBottom: 12 }}>
      <CardHead title="Carriers" sub={`${outbound.length} outbound`} />
      <div className="arc-card-body" style={{ paddingTop: 10, paddingBottom: 10 }}>
        {outbound.length === 0 ? (
          <div className="arc-banner arc-banner-warn" style={{ margin: 0 }}>
            <Icon name="warning" size={14} />
            <span>No outbound carrier: calls to outside numbers will fail. Link one under Trunks.</span>
          </div>
        ) : (
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {outbound
              .sort((a, b) => a.priority - b.priority)
              .map((l) => (
                <span key={l.id} className="arc-row" style={{ gap: 4 }}>
                  <span>
                    <span className="arc-num">{l.priority}</span> {l.carrier_name} · {l.trunk_name}
                  </span>
                  {/* Live, from the switch: configured is not the same as working. */}
                  <HealthBadge live={l.live} />
                  {l.auth_mode === 'register' && <RegistrationBadge live={l.live} authMode={l.auth_mode} />}
                </span>
              ))}
            {/* One carrier is no failover; worth saying before an outage says it. */}
            {outbound.length === 1 && (
              <span className="arc-badge arc-badge-warn">single carrier — no failover</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
